import { useState, useRef, useEffect } from "react";
import { useThemeStore, themes, type ThemeName } from "../../stores/themeStore";

export function MobileThemeButton() {
  const { theme, setTheme } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentTheme = themes.find((t) => t.name === theme) || themes[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleThemeChange = (themeName: ThemeName) => {
    setTheme(themeName);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Compact Theme Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg bg-surface-900/50 border border-surface-800/60 text-surface-200 active:scale-[0.97] transition-all"
        title={`Theme: ${currentTheme.label}`}
        aria-label="Select theme"
        aria-expanded={isOpen}
      >
        <div 
          className="w-5 h-5 rounded-full border border-surface-600/50"
          style={{ 
            background: `linear-gradient(135deg, ${currentTheme.preview.primary} 0%, ${currentTheme.preview.accent} 100%)`
          }}
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Modal-style dropdown for mobile */}
          <div
            className="fixed left-4 right-4 top-20 z-50 py-3 rounded-2xl shadow-2xl border border-surface-700/50 bg-surface-900/98 backdrop-blur-xl animate-slide-up max-h-[70vh] overflow-hidden flex flex-col"
            role="menu"
          >
            <div className="px-4 pb-3 border-b border-surface-700/50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-surface-100">Choose Theme</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 rounded-lg bg-surface-800/50 text-surface-400 hover:text-surface-200 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-3 space-y-2 overflow-y-auto flex-1 custom-scrollbar">
              {themes.map((t) => (
                <button
                  key={t.name}
                  onClick={() => handleThemeChange(t.name)}
                  className={`
                    w-full flex items-center gap-4 p-3 rounded-xl transition-all duration-200
                    ${theme === t.name 
                      ? "bg-primary-500/20 border-2 border-primary-500/40" 
                      : "bg-surface-800/30 border-2 border-transparent hover:border-surface-700/50"
                    }
                  `}
                  role="menuitem"
                  aria-current={theme === t.name ? "true" : "false"}
                >
                  {/* Large theme preview */}
                  <div className="relative flex-shrink-0">
                    <div 
                      className="w-14 h-14 rounded-xl border border-surface-600/30 overflow-hidden shadow-lg"
                      style={{ backgroundColor: t.preview.bg }}
                    >
                      {/* Mini card preview */}
                      <div 
                        className="absolute bottom-1.5 left-1.5 right-1.5 h-5 rounded"
                        style={{ backgroundColor: t.preview.card }}
                      />
                      {/* Primary color stripe */}
                      <div 
                        className="absolute top-2 left-2 w-3 h-3 rounded-full"
                        style={{ backgroundColor: t.preview.primary }}
                      />
                      {/* Accent color stripe */}
                      <div 
                        className="absolute top-2 right-2 w-3 h-3 rounded-full"
                        style={{ backgroundColor: t.preview.accent }}
                      />
                    </div>
                    
                    {/* Checkmark */}
                    {theme === t.name && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center shadow-lg">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Theme info */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`font-semibold ${theme === t.name ? "text-primary-400" : "text-surface-100"}`}>
                        {t.label}
                      </span>
                      {t.name === "arctic" && (
                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-surface-700/50 text-surface-300 uppercase tracking-wide">
                          Light
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-surface-400">
                      {t.description}
                    </span>
                  </div>
                </button>
              ))}
            </div>
            
            <div className="px-4 pt-3 border-t border-surface-700/50 flex-shrink-0">
              <p className="text-xs text-surface-500 text-center">
                Your preference is saved automatically
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
